import { Check, ArrowUpRight } from "lucide-react";
import { useInView } from "../hooks/useInView";

const tiers = [
  {
    name: "Basic",
    icon: "\u{1FAB4}",
    price: "$0",
    period: "Free forever",
    description: "For early-stage founders just starting to build their network.",
    highlight: false,
    features: [
      "Up to 250 Contacts",
      "Basic Pipeline (Kanban)",
      "Manual Data Entry",
      "1 Workspace",
      "Community Support",
    ],
    cta: "Get Started",
  },
  {
    name: "Pro",
    icon: "\u26A1",
    price: "$29",
    period: "per user / month",
    description: "For active founders who are actively fundraising or selling.",
    highlight: true,
    features: [
      "Unlimited Contacts & Pipelines",
      "Gmail Integration",
      "Intro Path Tracking",
      "100 AI Email Drafts/month",
      "Email Open Tracking",
      "Standard Support (24-48h)",
    ],
    cta: "Start Pro Trial",
  },
  {
    name: "Scale",
    icon: "\u{1F680}",
    price: "$79",
    period: "per user / month",
    description: "For power-users, serial founders, and small teams.",
    highlight: false,
    features: [
      "Everything in Pro, plus:",
      "Unlimited AI Email Drafts",
      "Advanced Relationship Graph",
      "LinkedIn Data Ingestion",
      "Custom Pipeline Stages",
      "Multi-User Collaboration",
      "Priority Support (same-day)",
    ],
    cta: "Start Scale Trial",
  },
];

export default function PricingSection() {
  const [sectionRef, isInView] = useInView();

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      ref={sectionRef}
      className="pl-8 md:pl-16 pr-4 md:pr-8 py-16 md:py-24"
    >
      {/* Header */}
      <div
        className={`mb-16 transition-all duration-700 ${
          isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#a33900] block mb-4">
          Pricing
        </span>
        <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#1c1b1d]">
          Simple pricing.
          <br />
          Scale as you grow.
        </h2>
        <p className="mt-4 text-base md:text-lg text-[#5a4138] max-w-lg">
          Start free. Upgrade when your network demands AI-powered intelligence.
        </p>
      </div>

      {/* Pricing cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl">
        {tiers.map((tier, i) => (
          <div
            key={tier.name}
            data-testid={`pricing-${tier.name.toLowerCase()}`}
            className={`
              rounded-3xl p-8 transition-all duration-500 relative
              ${tier.highlight
                ? "bg-[#f0edf0] ring-1 ring-[#e2bfb2]/30"
                : "bg-white"
              }
              ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}
            `}
            style={{ transitionDelay: `${i * 120 + 150}ms` }}
          >
            {tier.highlight && (
              <div className="absolute -top-3 left-8">
                <span className="bg-[#ff9971] text-[#772f0f] text-[10px] uppercase tracking-wider font-bold px-3 py-1 rounded-full">
                  Most Popular
                </span>
              </div>
            )}

            <div className="mb-6">
              <span className="text-2xl mb-2 block">{tier.icon}</span>
              <h3 className="text-lg font-medium text-[#1c1b1d]">{tier.name}</h3>
              <p className="text-sm text-[#5a4138] mt-1">{tier.description}</p>
            </div>

            <div className="mb-6">
              <span className="text-4xl font-semibold text-[#1c1b1d] tracking-tight">{tier.price}</span>
              <span className="text-sm text-[#5a4138] ml-1.5">{tier.period}</span>
            </div>

            <ul className="space-y-3 mb-8">
              {tier.features.map((feature) => (
                <li key={feature} className="flex items-start gap-2.5">
                  <Check size={14} className="text-[#a33900] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-[#5a4138]">{feature}</span>
                </li>
              ))}
            </ul>

            <a
              href="https://app.relio.in"
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
              className={`
                w-full inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-medium transition-all
                ${tier.highlight
                  ? "cta-gradient text-white shadow-[0_8px_20px_rgba(204,73,0,0.25)] hover:-translate-y-0.5"
                  : "bg-[#f6f2f5] text-[#1c1b1d] hover:bg-[#e2bfb2]/30"
                }
              `}
            >
              {tier.cta}
              <ArrowUpRight size={14} />
            </a>
          </div>
        ))}
      </div>
    </section>
  );
}
